import { currencyMinorUnits } from '@till-erp/platform/currencies';
import { formatMoney, isMoneyValue } from '@till-erp/settings/values';

const dateTime = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const longDate = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** „30.09.2026 09:42“ (Europe/Berlin). */
export function formatDateTime(iso: string | Date): string {
  const parts = dateTime.formatToParts(typeof iso === 'string' ? new Date(iso) : iso);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('day')}.${get('month')}.${get('year')} ${get('hour')}:${get('minute')}`;
}

/** „Donnerstag, 1. Oktober 2026“. */
export function formatLongDate(date: Date): string {
  return longDate.format(date);
}

/** Begrüßung nach Tageszeit in Berlin. */
export function greeting(date: Date): string {
  // Nur die Stunde aus den Teilen lesen: `format()` liefert „08 Uhr“, `Number()` daraus wäre NaN (immer „Guten Abend“).
  const hour = Number(
    new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', hour12: false })
      .formatToParts(date)
      .find((p) => p.type === 'hour')?.value,
  );
  if (hour < 11) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

/** „2027-02-01“ (fachliches Datum) → „01.02.2027“. */
export function formatDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return `${d}.${m}.${y}`;
}

/** Wert einer Einstellung kurz lesbar machen (eine Zeile, z. B. im Verlauf „alt → neu“). */
export function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'leer';
  if (typeof value === 'boolean') return value ? 'an' : 'aus';
  if (Array.isArray(value)) return value.length === 1 ? '1 Zeile' : `${value.length} Zeilen`;
  if (isMoneyValue(value)) return formatMoney(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Alter eines Klärfalls: „seit 5 Min.“, „seit 3 Std.“, „seit 2 Tagen“ (EXPERIENCE §4.2: Klärfälle nach Alter). */
export function formatAge(iso: string, now: Date = new Date()): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return 'seit gerade eben';
  if (minutes < 60) return `seit ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `seit ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'seit 1 Tag' : `seit ${days} Tagen`;
}

/** „gerade eben“ (unter 5 Sekunden), „vor 12 Sekunden“, „vor 3 Minuten“, „vor 2 Stunden“, „vor 3 Tagen“. */
export function formatAgo(iso: string, now: Date = new Date()): string {
  const seconds = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 1000));
  if (seconds < 5) return 'gerade eben';
  if (seconds < 120) return `vor ${seconds} Sekunden`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 120) return `vor ${minutes} Minuten`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `vor ${hours} Stunden`;
  return `vor ${Math.floor(hours / 24)} Tagen`;
}

/** Dateigröße deutsch: „812 Byte“, „200 KB“, „1,4 MB“ (Binärpräfix wie Windows). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Byte`;
  const kb = Math.round(bytes / 1024);
  if (kb < 1024) return `${kb.toLocaleString('de-DE')} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

/** Meldung „Datei zu groß“; knapp über der Grenze (gleich gerundet) „größer als die erlaubten 20 MB“ statt „20 MB groß; höchstens 20 MB“. */
export function fileTooLargeText(bytes: number, maxBytes: number): string {
  const size = formatFileSize(bytes);
  const limit = formatFileSize(maxBytes);
  return size === limit ? `Die Datei ist größer als die erlaubten ${limit}.` : `Die Datei ist ${size} groß; erlaubt sind höchstens ${limit}.`;
}

/** Nur die Uhrzeit „09:42“ (Europe/Berlin). */
export function formatTime(iso: string | Date): string {
  return new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' }).format(
    typeof iso === 'string' ? new Date(iso) : iso,
  );
}

const berlinWeekday = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Berlin', weekday: 'short' });
const weekdayShort: Record<string, string> = { Mon: 'Mo', Tue: 'Di', Wed: 'Mi', Thu: 'Do', Fri: 'Fr', Sat: 'Sa', Sun: 'So' };

/** „Mo 01.03.2027, 07:00“ (Europe/Berlin) – Band und Seite der Testuhr. */
export function formatWeekdayDateTime(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  const [day, time] = formatDateTime(date).split(' ');
  return `${weekdayShort[berlinWeekday.format(date)] ?? ''} ${day}, ${time}`;
}

const berlinIsoDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' });

/** Fachliches Datum `YYYY-MM-DD` (Europe/Berlin) eines Zeitpunkts. */
export function isoDateInBerlin(date: Date): string {
  return berlinIsoDate.format(date);
}

/** Berliner Kalendertag vor `now` als ISO-Datum – nie „jetzt − 24 h“ (Zeitumstellung: Tage mit 23 oder 25 Stunden). */
export function previousIsoDateInBerlin(now: Date): string {
  const day = new Date(`${isoDateInBerlin(now)}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() - 1);
  return day.toISOString().slice(0, 10);
}

/** Technischer Zeitpunkt kurz: heute nur „09:42“, sonst „30.09.2026 09:42“ (Europe/Berlin). */
export function formatWhen(iso: string | Date, now: Date = new Date()): string {
  const full = formatDateTime(iso);
  return full.slice(0, 10) === formatDateTime(now).slice(0, 10) ? formatTime(iso) : full;
}

/**
 * Einzelpreis (Dezimaltext mit bis zu 4 Nachkommastellen) deutsch mit Währung: mindestens so viele Nachkommastellen wie die
 * Währung hat, weitere nur, wenn sie nicht 0 sind („38.9000“ → „38,90 €“, „1.2345“ → „1,2345 €“).
 */
export function formatUnitPrice(value: string, currency: string): string {
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!m) return `${value} ${currency}`;
  const [, sign, int, frac = ''] = m;
  const minor = currencyMinorUnits(currency);
  const trimmed = frac.replace(/0+$/, '');
  const digits = trimmed.length > minor ? trimmed : frac.padEnd(minor, '0').slice(0, minor);
  const grouped = int!.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const symbol = currency === 'EUR' ? '€' : currency;
  return `${sign ? '−' : ''}${grouped}${digits ? `,${digits}` : ''} ${symbol}`;
}

/** Einzelpreise (Dezimaltext, bis 4 Stellen wie `numeric(18,4)`) verschieden? Vergleich in voller Genauigkeit, ungültig → `false`. */
export function unitPriceChanged(a: string, b: string): boolean {
  const tenThousandths = (value: string): bigint | null => {
    const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
    if (!m) return null;
    const v = BigInt(m[2]!) * 10000n + BigInt((m[3] ?? '').padEnd(4, '0').slice(0, 4));
    return m[1] ? -v : v;
  };
  const x = tenThousandths(a);
  const y = tenThousandths(b);
  return x !== null && y !== null && x !== y;
}
